import React, { useRef, useEffect } from 'react';
import Modal from "react-modal";
import { X, Archive, RotateCcw, Trash2, Ban, UserX, EyeOff } from "lucide-react";

Modal.setAppElement("#root");

const ActionModal = ({ 
  isOpen, 
  onRequestClose, 
  onConfirm, 
  title = "this item",
  itemName = "item",
  isLoading = false,
  actionType = "archive", // archive, restore, delete, suspend, remove, disable, hide
  primaryColor = "#3ED468", // Default green for archive
  dangerColor = "#DC2626", // Red for delete/suspend/remove
  customIcon = null,
  customTitle = null,
  customMessage = null,
  customDescription = null,
  customButtonText = null,
}) => {
  const clickProcessedRef = useRef(false);

  const handleConfirm = () => {
    if (clickProcessedRef.current || isLoading) return;
    
    clickProcessedRef.current = true;
    
    if (onConfirm) {
      Promise.resolve(onConfirm()).finally(() => {
        clickProcessedRef.current = false;
      });
    }
  };

  useEffect(() => {
    if (!isOpen) {
      clickProcessedRef.current = false;
    }
  }, [isOpen]);

  // Get color based on action type
  const getActionColor = () => {
    const dangerActions = ["delete", "suspend", "remove", "disable", "hide", "block"];
    if (dangerActions.includes(actionType)) {
      return dangerColor;
    }
    return primaryColor;
  };

  // Get icon based on action
  const getIcon = () => {
    if (customIcon) return customIcon;
    
    const icons = {
      archive: <Archive size={40} />,
      restore: <RotateCcw size={40} />,
      delete: <Trash2 size={40} />,
      suspend: <Ban size={40} />,
      remove: <UserX size={40} />,
      disable: <EyeOff size={40} />,
      hide: <EyeOff size={40} />,
      block: <Ban size={40} />
    };
    return icons[actionType] || icons.archive;
  };

  // Get title based on action
  const getTitle = () => {
    if (customTitle) return customTitle;
    
    const titles = {
      archive: `Archive ${itemName}`,
      restore: `Restore ${itemName}`,
      delete: `Delete ${itemName}`,
      suspend: `Suspend ${itemName}`,
      remove: `Remove ${itemName}`,
      disable: `Disable ${itemName}`,
      hide: `Hide ${itemName}`,
      block: `Block ${itemName}`
    };
    return titles[actionType] || `${actionType.charAt(0).toUpperCase() + actionType.slice(1)} ${itemName}`;
  };

  // Get button text based on action
  const getButtonText = () => {
    if (customButtonText) return customButtonText;
    
    const buttonTexts = {
      archive: "Archive",
      restore: "Restore",
      delete: "Delete",
      suspend: "Suspend",
      remove: "Remove",
      disable: "Disable",
      hide: "Hide",
      block: "Block"
    };
    return buttonTexts[actionType] || actionType.charAt(0).toUpperCase() + actionType.slice(1);
  };

  // Get loading text based on action
  const getLoadingText = () => {
    const loadingTexts = {
      archive: "Archiving...",
      restore: "Restoring...",
      delete: "Deleting...",
      suspend: "Suspending...",
      remove: "Removing...",
      disable: "Disabling...",
      hide: "Hiding...",
      block: "Blocking..."
    };
    return loadingTexts[actionType] || `${getButtonText()}ing...`;
  };

  // Get message based on action
  const getMessage = () => {
    if (customMessage) return customMessage;
    
    const messages = {
      archive: `Are you sure you want to archive "${title}"?`,
      restore: `Are you sure you want to restore "${title}"?`,
      delete: `Are you sure you want to delete "${title}"?`,
      suspend: `Are you sure you want to suspend "${title}"?`,
      remove: `Are you sure you want to remove "${title}"?`,
      disable: `Are you sure you want to disable "${title}"?`,
      hide: `Are you sure you want to hide "${title}"?`,
      block: `Are you sure you want to block "${title}"?`
    };
    return messages[actionType] || `Are you sure you want to ${actionType} "${title}"?`;
  };

  // Get description based on action
  const getDescription = () => {
    if (customDescription) return customDescription;
    
    const descriptions = {
      archive: "This item will be moved to archive and won't be visible to users.",
      restore: "This item will be restored and visible to users again.",
      delete: "This action cannot be undone. This item will be permanently deleted.",
      suspend: "This item will be temporarily suspended and won't be accessible.",
      remove: "This item will be permanently removed from the system.",
      disable: "This item will be disabled and won't function until re-enabled.",
      hide: "This item will be hidden from public view.",
      block: "This user/item will be blocked and won't be able to perform actions."
    };
    return descriptions[actionType] || `Are you sure you want to ${actionType} this item?`;
  };

  const actionColor = getActionColor();

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel={getTitle()}
overlayClassName="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      className="bg-white w-full max-w-[500px] rounded-xl shadow-lg p-6 outline-none relative"
    >
      {/* Close Button */}
      <button
        onClick={onRequestClose}
        className="text-gray-400 cursor-pointer hover:text-gray-600 transition-colors absolute right-6 top-6"
        disabled={isLoading}
      >
        <X size={28} />
      </button>

      {/* Icon Section */}
      <div className="flex justify-center items-center mb-5 mt-4">
        <div 
          className="w-20 h-20 flex items-center justify-center border-2 rounded-sm"
          style={{ 
            borderColor: actionColor,
            backgroundColor: `${actionColor}10` // 10% opacity of the color
          }}
        >
          <div style={{ color: actionColor }}>
            {getIcon()}
          </div>
        </div>
      </div>

      {/* Title Section */}
      <div className="flex justify-between items-center mb-4 relative">
        <div className="flex-1 flex justify-center">
          <div className="text-center">
            <h2 className="text-[25px] font-semibold" style={{ color: actionColor }}>
              {getTitle()}
            </h2>
          </div>
        </div>
      </div>

      {/* Message */}
      <div className="mb-4">
        <div className="text-center">
          <p className="text-gray-700 text-lg">
            {getMessage()}
          </p>
          <p className="text-gray-500 text-sm mt-2">
            {getDescription()}
          </p>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-4">
        <button
          onClick={onRequestClose}
          disabled={isLoading}
          className="flex-1 cursor-pointer text-lg border-2 border-gray-300 text-gray-600 font-medium py-3 rounded-xs hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          disabled={isLoading}
          className="flex-1 cursor-pointer text-lg text-white font-medium py-3 rounded-xs transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
          style={{ backgroundColor: actionColor }}
        >
          {isLoading ? (
            <>
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              <span>{getLoadingText()}</span>
            </>
          ) : (
            <span className="flex items-center gap-2">
              {getButtonText()} {itemName}
            </span>
          )}
        </button>
      </div>
    </Modal>
  );
};

export default ActionModal;